import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Network,
    Globe,
    Monitor,
    Shield,
    Zap,
    Server,
    ArrowRight,
    CheckCircle,
    Users,
    Clock,
    Award,
} from 'lucide-react';

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
};

const staggerContainer = {
    animate: {
        transition: { staggerChildren: 0.1 },
    },
};

const HomePage = () => {
    const services = [
        {
            icon: Network,
            title: 'Network Infrastructure',
            description: 'Structured cabling, fiber optics, and complete comms room design & management.',
        },
        {
            icon: Globe,
            title: 'Global Connectivity',
            description: 'Unified communications, global telephony architecture, and SIP trunking solutions.',
        },
        {
            icon: Monitor,
            title: 'Audio-Visual Solutions',
            description: 'State-of-the-art AV systems for modern enterprise environments.',
        },
        {
            icon: Shield,
            title: 'Security Systems',
            description: 'Video surveillance, access control, and automated fire detection systems.',
        },
        {
            icon: Zap,
            title: 'Power Solutions',
            description: 'UPS distribution, renewable energy infrastructure, and solar deployments.',
        },
        {
            icon: Server,
            title: 'Managed Services',
            description: 'Digital business automation, IT service management, and M365 solutions.',
        },
    ];

    const stats = [
        { number: '30+', label: 'Years Experience', icon: Clock },
        { number: '500+', label: 'Projects Delivered', icon: CheckCircle },
        { number: '200+', label: 'Enterprise Clients', icon: Users },
        { number: '99%', label: 'Client Satisfaction', icon: Award },
    ];

    return (
        <div data-testid="home-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                        alt="Modern server room"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/80 to-slate-900/95"></div>
                </div>

                {/* Content */}
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={staggerContainer}
                        className="max-w-4xl"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="inline-block font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6"
                        >
                            Infrastructure | Technology | Solutions and Services
                        </motion.span>

                        <motion.h1
                            variants={fadeUp}
                            className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-8"
                            data-testid="hero-title"
                        >
                            Building the Backbone of{' '}
                            <span className="text-blue-400">Digital Enterprise</span>
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-slate-300 max-w-2xl mb-10 leading-relaxed"
                        >
                            For three decades, Vanfrank has delivered highly customized technology services
                            that help organizations improve efficiency, reduce operational costs, and
                            maximize business value.
                        </motion.p>

                        <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                            <Link
                                to="/services"
                                data-testid="hero-cta-services"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-colors shadow-[0_0_30px_rgba(59,130,246,0.4)]"
                            >
                                Explore Solutions
                                <ArrowRight size={18} />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-contact"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white border border-white/20 rounded-full font-semibold hover:bg-white/20 transition-colors backdrop-blur-sm"
                            >
                                Get In Touch
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
                    <motion.div
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
                    >
                        <div className="w-1.5 h-3 bg-white/50 rounded-full"></div>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="relative -mt-20 z-20 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <div
                        data-testid="stats-section"
                        className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
                    >
                        {stats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-slate-100"
                            >
                                <stat.icon className="text-blue-500 mb-4" size={28} />
                                <div className="font-mono text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-slate-500 text-sm font-medium">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Preview Section */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                                About Vanfrank
                            </span>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                                Three Decades of Excellence in ICT Solutions
                            </h2>
                            <p className="text-slate-600 leading-relaxed mb-6">
                                Vanfrank is an end-to-end Network Infrastructure and Information Communications
                                Technology Solutions Provider. We deliver highly customized technology services
                                that integrate people, processes, and technology into a unified framework.
                            </p>
                            <p className="text-slate-600 leading-relaxed mb-8">
                                Our expertise spans Network Infrastructure, IT Service Management, Unified
                                Communications, Data Center Design, Enterprise Connectivity, Security Systems,
                                and Technology-driven Business Solutions.
                            </p>
                            <Link
                                to="/about"
                                data-testid="about-cta"
                                className="inline-flex items-center gap-2 text-blue-500 font-semibold hover:gap-4 transition-all"
                            >
                                Learn More About Us
                                <ArrowRight size={18} />
                            </Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="relative"
                        >
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600">
                                <img
                                    src="https://customer-assets.emergentagent.com/job_8b312a74-6750-4e40-9116-cda52d8face7/artifacts/mehw8gc9_shutterstock_2746011105.jpg"
                                    alt="Vanfrank team collaboration"
                                    className="w-full h-[400px] object-cover mix-blend-luminosity opacity-40"
                                    loading="lazy"
                                />
                            </div>
                            {/* Decorative Element */}
                            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-blue-500/10 rounded-2xl -z-10"></div>
                            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-blue-500/20 rounded-2xl -z-10"></div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-24 md:py-32 bg-slate-50 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                            Core Competencies
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                            Comprehensive ICT Solutions
                        </h2>
                        <p className="text-slate-600 max-w-2xl mx-auto">
                            We deliver technology solutions that directly support business strategy,
                            ensuring clear identification of business priorities and effective service management.
                        </p>
                    </motion.div>

                    <div data-testid="services-grid" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, index) => (
                            <motion.div
                                key={service.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="group bg-white rounded-2xl p-8 border border-slate-100 hover:border-blue-500/30 hover:shadow-xl transition-all duration-300"
                            >
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <service.icon className="text-white" size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                                <p className="text-slate-600 leading-relaxed">{service.description}</p>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mt-12"
                    >
                        <Link
                            to="/services"
                            data-testid="services-cta"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-colors shadow-[0_0_30px_rgba(15,23,42,0.3)]"
                        >
                            View All Solutions
                            <ArrowRight size={18} />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Vision & Mission Section */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-8">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            data-testid="vision-card"
                            className="bg-slate-900 rounded-2xl p-8 md:p-12"
                        >
                            <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-4 block">
                                Our Vision
                            </span>
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                To consistently enhance productivity.
                            </h3>
                            <p className="text-slate-400 leading-relaxed">
                                We envision a future where every organization leverages technology
                                to achieve operational excellence and sustainable growth.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            data-testid="mission-card"
                            className="bg-blue-500 rounded-2xl p-8 md:p-12"
                        >
                            <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-200 mb-4 block">
                                Our Mission
                            </span>
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                To enrich businesses through customized solutions.
                            </h3>
                            <p className="text-blue-100 leading-relaxed">
                                We drive sustainable profitability for our clientele and ourselves
                                through innovative, tailored technology solutions.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 md:py-32 bg-slate-900 px-6 md:px-12 lg:px-24 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0 grid-pattern"></div>
                </div>

                <div className="relative max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6 block">
                            Ready to Transform?
                        </span>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                            Let's Build Your Digital Infrastructure Together
                        </h2>
                        <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
                            Partner with Vanfrank to enhance productivity, reduce costs, and maximize
                            business value through cutting-edge ICT solutions.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="cta-contact"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-colors shadow-[0_0_30px_rgba(59,130,246,0.4)]"
                            >
                                Start a Conversation
                                <ArrowRight size={18} />
                            </Link>
                            <Link
                                to="/solutions"
                                data-testid="cta-solutions"
                                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white border border-white/20 rounded-full font-semibold hover:bg-white/20 transition-colors"
                            >
                                Explore Service Delivery
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
